import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import RuleTable from "./rule-table";
import RuleCard from "./rule-card/rule-card";
import ApiRuleManagement from "../../../api/rule-managements";
import useNotification from "../../../components/notification/notification";
import "./styles.css";
import { Body } from "../../../components/main-body/body";
import { Modal } from "antd";
import { useParams, useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import { useBulkSelection } from "../../../common/utils/useBulkSelection";
import BulkSelectionBar from "../../../components/table/bulk-selection-bar";
import useAdminEvents from "../../../hooks/useAdminEvents";
import { useCan } from "../../../common/utils/usePermissions";

const DeleteModalComponent = ({ title, handleOk, handleCancel, isModalOpen }) => {
  return (
    <Modal
      title={title}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      okButtonProps={{ danger: true }}
      okText="Delete"
      className="plan-page-action-modal"
    >
      <p>Are you sure you want to delete this rule?</p>
    </Modal>
  );
};

const RuleManagement = ({ mode }) => {
  const [rules, setRules] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const [isOpen, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRule, setSelectedRule] = useState({});
  const [ruleFilters, setRuleFilters] = useState({});
  const [isBulkDeleting, setBulkDeleting] = useState(false);
  const { t } = useTranslation();
  // LF360-2024: creating needs `content.edit` on the server.
  const canCreate = useCan("content.edit");
  const { openNotification } = useNotification();
  const [isOpenDeleteRuleModal, setOpenDeleteRuleModal] = useState();

  const { id } = useParams();
  const navigate = useProjectNavigate();
  const location = useLocation();

  const inflightRef = useRef(false);
  const lastQueryRef = useRef({ page: null });

  const { selectedKeys, clearSelection, getSelectionColumn, setSelectedKeys } =
    useBulkSelection(rules, { rowKey: "id" });

  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const handleNavigate = (path) => {
    if (location.pathname !== path) navigate(path);
  };

  // ---- API ----
  const getRules = useCallback(
    async ({ append = false, pageParam } = {}) => {
      const currentPage = pageParam ?? page;

      if (inflightRef.current) return;
      if (lastQueryRef.current.page === currentPage && !append) return;

      inflightRef.current = true;
      setLoading(true);
      try {
        const { data } = await ApiRuleManagement.getRules({ page: currentPage });
        const { scoringRules = [], ruleFilters: dictionaries, hasMore: nextHasMore } = data || {};

        setRules(prev => (append ? [...prev, ...scoringRules] : scoringRules));
        setHasMore(!!nextHasMore);

        if (currentPage === 1 && dictionaries) {
          setRuleFilters(dictionaries);
        }

        lastQueryRef.current = { page: currentPage };
      } finally {
        setLoading(false);
        inflightRef.current = false;
      }
    },
    [page]
  );

  // ---- init ----
  useEffect(() => {
    getRules({ append: false, pageParam: 1 });
  }, []); // mount only

  useEffect(() => {
    if (page > 1) getRules({ append: true });
  }, [page]); // only on page change

  const refreshRules = useCallback(() => {
    lastQueryRef.current = { page: null };
    setPage(1);
    return getRules({ append: false, pageParam: 1 });
  }, [getRules]);

  useAdminEvents({
    onTagChanged: refreshRules,
    onCategoryChanged: refreshRules,
    onFitFactChanged: refreshRules,
    onArticleChanged: refreshRules,
    onTaskChanged: refreshRules,
    onRuleChanged: (payload) => {
      refreshRules();

      const changedIds = [
        ...(payload?.deletedIds || []),
        payload?.id,
      ].filter(Boolean);

      setSelectedRule((prev) => {
        if (!prev?.id || !changedIds.includes(prev.id)) return prev;

        if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
          handleNavigate("/content/rules");
          setOpen(false);
          return {};
        }

        return payload?.scoringRule ? { ...prev, ...payload.scoringRule } : prev;
      });
    },
  });

  useEffect(() => {
    if (id && rules.length) {
      const rule = rules.find(r => r.id === id);
      if (rule) {
        setSelectedRule(rule);
        setOpen(true);
        return;
      }
    }

    setSelectedRule({});
    if (!id && isOpen) setOpen(false);
  }, [id, rules]); // sync with route & list

  useEffect(() => {
    setEditMode(mode === "edit" || mode === "create");
    if (mode === "create") {
      setOpen(true);
      setSelectedRule({});
    }
  }, [mode]);

  // ---- actions ----
  const openRuleDrawer = (edit) => {
    if (edit) setEditMode(true);
    if (selectedRule?.id) {
      handleNavigate(`/content/rules/${selectedRule.id}${edit ? "/edit" : ""}`);
    } else {
      handleNavigate(`/content/rules/create`);
    }
    setOpen(true);
  };

  const cancelEditing = () => {
    handleNavigate(`/content/rules/${selectedRule.id}`);
    setEditMode(false);
  };

  const closeRuleDrawer = () => {
    handleNavigate(`/content/rules`);
    setEditMode(false);
    setOpen(false);
    setSelectedRule({});
  };

  const createRule = (rule) => {
    setEditMode(false);
    ApiRuleManagement.createRule(rule)
      .then((response) => {
        const createdRuleId = response.data.scoringRule.id;
        lastQueryRef.current = { page: null };
        setPage(1);
        getRules({ append: false, pageParam: 1 }).then(() => {
          openNotification({
            type: "success",
            message: <p>{t("rule_management.notification.created", { rule: response.data.scoringRule.title })}</p>,
          });
          handleNavigate(`/content/rules/${createdRuleId}`);
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t(`rule_management.notification.${e?.response?.data?.error?.code || "error"}`)}
            </p>
          ),
        });
      });
  };

  const updateRule = (id, rule) => {
    setEditMode(false);
    handleNavigate(`/content/rules/${rule.id}`);

    ApiRuleManagement.updateRule(id, rule)
      .then(() => {
        lastQueryRef.current = { page: null };
        setPage(1);
        getRules({ append: false, pageParam: 1 }).then(() => {
          openNotification({
            type: "success",
            message: <p>{t("rule_management.notification.updated", { rule: rule.title })}</p>,
          });
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t(`rule_management.notification.${e?.response?.data?.error?.code || "error"}`)}
            </p>
          ),
        });
      });
  };

  const deleteRule = (id, title) => {
    closeRuleDrawer();
    ApiRuleManagement.deleteRule(id)
      .then(() => {
        lastQueryRef.current = { page: null };
        setPage(1);
        return getRules({ append: false, pageParam: 1 });
      })
      .then(() => {
        openNotification({
          type: "success",
          message: <p>{t("rule_management.notification.deleted", { rule: title })}</p>,
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t(`rule_management.notification.${e?.response?.data?.error?.code || "error"}`)}
            </p>
          ),
        });
      });
  };

  const handleSelectRule = (rule) => {
    handleNavigate(`/content/rules/${rule.id}`);
    setSelectedRule(rule);
    setOpen(true);
  };

  const toEditMode = () => {
    handleNavigate(`/content/rules/${selectedRule.id}/edit`);
    setEditMode(true);
  };

  const openDeleteModal = () => setOpenDeleteRuleModal(true);
  const closeDeleteModal = () => setOpenDeleteRuleModal(false);
  const handleDeleteOk = () => { deleteRule(selectedRule.id, selectedRule.title); closeDeleteModal(); };
  const handleDeleteCancel = () => closeDeleteModal();

  const headerProps = useMemo(
    () => ({
      title: "Content blocking rules",
      create: canCreate && {
        label: t("rule_management.create"),
        onClick: () => openRuleDrawer(true),
      },
    }),
    [isOpen, t, canCreate]
  );

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    try {
      const resp = await ApiRuleManagement.removeBulk(selectedKeys);
      const { deletedIds = [], failed = [] } = resp?.data || {};
      const failedIds = failed.map((f) => f.id);

      if (deletedIds.length) {
        const plural = deletedIds.length > 1;
        openNotification?.({
          type: "success",
          message: (
            <p>
              {deletedIds.length} {plural ? "rules were" : "rule was"} deleted
            </p>
          ),
        });
      }

      if (failedIds.length) {
        const plural = failedIds.length > 1;
        openNotification?.({
          type: "error",
          message: (
            <p>
              Unable to delete {failedIds.length} {plural ? "rules" : "rule"}
            </p>
          ),
        });
      }

      if (!failedIds.length) {
        clearSelection();
      } else {
        setSelectedKeys(failedIds);
      }

      lastQueryRef.current = { page: null };
      setPage(1);
      await getRules({ append: false, pageParam: 1 });
    } catch (e) {
      console.error(e);
      openNotification?.({
        type: "error",
        message: <p>Failed to delete rules</p>,
      });
    } finally {
      setBulkDeleting(false);
    }
  }, [
    selectedKeys,
    isBulkDeleting,
    openNotification,
    clearSelection,
    setSelectedKeys,
    getRules,
  ]);

  return (
    <Body header={headerProps}>
      <RuleTable
        data={rules}
        onHandleRowClick={handleSelectRule}
        isLoading={loading}
        hasMore={hasMore}
        fetchData={() => {
          if (!loading && hasMore) setPage(p => p + 1);
        }}
        selectionColumn={getSelectionColumn()}
        containerRef={tableRef}
        after={
          <BulkSelectionBar
            left={bulkLeft}
            count={selectedKeys.length}
            onClear={clearSelection}
            onDelete={handleBulkDelete}
            loading={isBulkDeleting}
          />
        }
      />
      <RuleCard
        ruleFilters={ruleFilters}
        rule={selectedRule}
        isOpen={isOpen}
        close={closeRuleDrawer}
        editMode={editMode}
        toEditMode={toEditMode}
        createRule={createRule}
        updateRule={updateRule}
        deleteRule={openDeleteModal}
        cancelEditing={cancelEditing}
      />
      <DeleteModalComponent
        isModalOpen={isOpenDeleteRuleModal}
        handleOk={handleDeleteOk}
        handleCancel={handleDeleteCancel}
        title={selectedRule.title}
      />
    </Body>
  );
};

export default RuleManagement;
